// @ts-nocheck
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RichTextDisplay } from "@/design-system/forms/RichTextEditor";
import { Loader2, Check, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";
import { formatCalendarDate } from "@/lib/dates";
import { getRlTeams, registrationTeamId, resolveRegistrationTeamNames, storedRegistrationTeamName } from "@/lib/rlTeams";

const sb = supabase as any;
const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (d?: string | null) => formatCalendarDate(d, "M/d/yyyy", "—");

export default function MyRegistrationsDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [instructionsModal, setInstructionsModal] = useState<any | null>(null);

  // Registrations
  const { data: regs, isLoading: regsLoading, refetch: refetchRegs } = useQuery({
    queryKey: ["my-registrations", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await sb
        .from("event_registrations_v2")
        .select(
          `*, events(id,name,slug,start_date,end_date,pay_later_instructions,pay_later_late_fee)`
        )
        .or(
          `initiated_by_user_id.eq.${user.id},primary_billing_user_id.eq.${user.id}`
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = data ?? [];
      const names = await resolveRegistrationTeamNames(rows);
      return rows.map((row: any) => ({ ...row, resolved_team_name: names[registrationTeamId(row)] || storedRegistrationTeamName(row) }));
    },
  });

  // Teams
  const { data: teams } = useQuery({
    queryKey: ["my-teams", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await sb
        .from("team_members")
        .select(`*`)
        .eq("user_id", user.id)
        .eq("status", "active")
        .is("removed_at", null);
      if (error) throw error;
      const rows = data ?? [];
      let resolved: Record<string, any> = {};
      try { resolved = await getRlTeams(rows.map((r: any) => r.team_id).filter(Boolean)); } catch { /* safe fallback */ }
      return rows.map((row: any) => ({ ...row, resolved_team: resolved[row.team_id] }));
    },
  });

  // Invitations
  const { data: invites, refetch: refetchInvites } = useQuery({
    queryKey: ["my-invitations", user?.email],
    enabled: !!user?.email,
    queryFn: async () => {
      const { data, error } = await sb
        .from("team_member_invitations")
        .select(`*`)
        .eq("invitee_email", user.email.toLowerCase())
        .eq("status", "pending");
      if (error) throw error;
      const rows = data ?? [];
      let resolved: Record<string, any> = {};
      try { resolved = await getRlTeams(rows.map((r: any) => r.team_id).filter(Boolean)); } catch { /* safe fallback */ }
      return rows.map((row: any) => ({ ...row, resolved_team: resolved[row.team_id] }));
    },
  });

  // Credits
  const { data: credits } = useQuery({
    queryKey: ["my-credits", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await sb
        .from("organization_credits")
        .select("*")
        .eq("user_id", user.id);
      return data ?? [];
    },
  });

  const groupedByEvent = useMemo(() => {
    const map = new Map<string, any[]>();
    (regs ?? []).forEach((r: any) => {
      const k = r.events?.id ?? r.event_id;
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    });
    return Array.from(map.entries());
  }, [regs]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Please sign in to view your dashboard.</p>
      </div>
    );
  }

  const declineInvite = async (id: string) => {
    await sb.from("team_member_invitations").update({ status: "declined" }).eq("id", id);
    toast.success("Invitation declined");
    refetchInvites();
  };

  return (
    <div className="container max-w-5xl mx-auto py-6 px-4">
      <h1 className="text-2xl font-bold mb-6">My Dashboard</h1>

      <Tabs defaultValue="registrations">
        <TabsList>
          <TabsTrigger value="registrations">My Registrations</TabsTrigger>
          <TabsTrigger value="teams">My Teams</TabsTrigger>
          <TabsTrigger value="invites">
            Pending Invitations
            {invites && invites.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {invites.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="credits">Credits</TabsTrigger>
        </TabsList>

        {/* REGISTRATIONS */}
        <TabsContent value="registrations" className="space-y-4 mt-4">
          {regsLoading && <Loader2 className="w-5 h-5 animate-spin" />}
          {!regsLoading && groupedByEvent.length === 0 && (
            <p className="text-sm text-muted-foreground">
              You have no registrations yet.
            </p>
          )}
          {groupedByEvent.map(([eventId, eventRegs]) => {
            const ev = eventRegs[0].events;
            return (
              <Card key={eventId}>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <h2 className="font-semibold">{ev?.name ?? "Event"}</h2>
                      <p className="text-xs text-muted-foreground">
                        {fmtDate(ev?.start_date)} – {fmtDate(ev?.end_date)}
                      </p>
                    </div>
                    {ev?.slug && (
                      <Link
                        to={`/events/${ev.slug}`}
                        className="text-xs text-primary hover:underline"
                      >
                        View event →
                      </Link>
                    )}
                  </div>
                  <div className="space-y-2">
                    {eventRegs.map((r: any) => (
                      <RegistrationCard
                        key={r.id}
                        reg={r}
                        onShowInstructions={() => setInstructionsModal(ev)}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* TEAMS */}
        <TabsContent value="teams" className="space-y-3 mt-4">
          {(teams ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">
              You are not on any teams.
            </p>
          )}
          {(teams ?? []).map((tm: any) => (
            <Card key={tm.id}>
              <CardContent className="pt-4 flex justify-between items-center gap-2">
                <div>
                  <div className="font-medium">
                    {tm.resolved_team?.display_name ?? tm.resolved_team?.name ?? "Team"}
                  </div>
                  <div className="text-xs text-muted-foreground capitalize">
                    {tm.role?.replace(/_/g, " ")} ·{" "}
                    {tm.resolved_team?.age_group ?? ""} {tm.resolved_team?.gender ?? ""}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* INVITATIONS */}
        <TabsContent value="invites" className="space-y-3 mt-4">
          {(invites ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">
              No pending invitations.
            </p>
          )}
          {(invites ?? []).map((inv: any) => (
            <Card key={inv.id}>
              <CardContent className="pt-4 flex justify-between items-center gap-2">
                <div>
                  <div className="font-medium">{inv.resolved_team?.display_name ?? inv.resolved_team?.name ?? "Team"}</div>
                  <div className="text-xs text-muted-foreground capitalize">
                    Role: {inv.role?.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => declineInvite(inv.id)}>
                    Decline
                  </Button>
                  <Button size="sm" disabled title="Use the link in your email to accept">
                    Accept (via email)
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* CREDITS */}
        <TabsContent value="credits" className="space-y-3 mt-4">
          {(credits ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">No credits available.</p>
          )}
          {(credits ?? []).map((c: any) => (
            <Card key={c.id}>
              <CardContent className="pt-4 flex justify-between items-center">
                <div className="text-sm">Balance</div>
                <div className="font-semibold">{money(c.balance ?? 0)}</div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog
        open={!!instructionsModal}
        onOpenChange={(o) => !o && setInstructionsModal(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment instructions</DialogTitle>
          </DialogHeader>
          {instructionsModal?.pay_later_instructions ? (
            <RichTextDisplay
              html={instructionsModal.pay_later_instructions}
              className="text-sm"
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              No instructions provided. Contact the event organizer.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RegistrationCard({
  reg,
  onShowInstructions,
}: {
  reg: any;
  onShowInstructions: () => void;
}) {
  const teamName = reg.resolved_team_name ?? storedRegistrationTeamName(reg);
  const method = reg.payment_method_chosen ?? "online";
  const status = reg.payment_status;
  const isLater = method !== "online";
  const dueAt = reg.payment_due_at ? new Date(reg.payment_due_at) : null;
  const overdue =
    isLater && status === "pending" && dueAt && dueAt.getTime() < Date.now();
  const lateFee = Number(reg.events?.pay_later_late_fee ?? 0);

  let chip: React.ReactNode = null;
  let body: React.ReactNode = null;

  if (!isLater && status === "paid") {
    chip = (
      <Badge className="bg-green-600 text-white">
        <Check className="w-3 h-3 mr-1" />
        Paid online
      </Badge>
    );
    body = (
      <p className="text-xs text-muted-foreground">
        Paid on {fmtDate(reg.payment_received_at ?? reg.created_at)}
      </p>
    );
  } else if (isLater && status === "paid") {
    chip = (
      <Badge className="bg-green-600 text-white">
        <Check className="w-3 h-3 mr-1" />
        Paid
      </Badge>
    );
    body = (
      <p className="text-xs text-muted-foreground">
        Paid by {reg.payment_received_method ?? method} on{" "}
        {fmtDate(reg.payment_received_at)}
        {reg.payment_received_reference
          ? ` · Ref: ${reg.payment_received_reference}`
          : ""}
      </p>
    );
  } else if (isLater && overdue) {
    chip = (
      <Badge variant="destructive">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Overdue
      </Badge>
    );
    body = (
      <div className="space-y-1">
        <p className="text-xs text-red-700">
          Payment was due {fmtDate(reg.payment_due_at)}.
          {lateFee > 0 ? ` Late fee: ${money(lateFee)}` : ""}
        </p>
        <Button size="sm" variant="outline" onClick={onShowInstructions}>
          View payment instructions
        </Button>
      </div>
    );
  } else if (isLater && status === "pending") {
    chip = (
      <Badge className="bg-amber-500 text-white">
        <Clock className="w-3 h-3 mr-1" />
        Pay later
      </Badge>
    );
    body = (
      <div className="space-y-1">
        <p className="text-xs">
          {money(reg.total_amount_due)} due {dueAt ? fmtDate(reg.payment_due_at) : "soon"}{" "}
          via <span className="capitalize">{method}</span>
        </p>
        <Button size="sm" variant="outline" onClick={onShowInstructions}>
          View payment instructions
        </Button>
      </div>
    );
  } else {
    chip = <Badge variant="outline">{status}</Badge>;
    body = (
      <p className="text-xs text-muted-foreground">
        Total: {money(reg.total_amount_due)}
      </p>
    );
  }

  return (
    <div className="p-3 border border-border rounded">
      <div className="flex justify-between items-start gap-2">
        <div className="font-medium text-sm">{teamName}</div>
        {chip}
      </div>
      <div className="mt-1">{body}</div>
    </div>
  );
}
